import { useCallback } from "react";
import type { Collab } from "@cabal/shared/domain/session/collab/types.js";
import { OWN_COLLAB_READ_ADVANCE_BUFFER_MS } from "@/types/frontend-types";
import type {
  AddOwnMessageIds,
  TagOwnCollabsAndAdvanceReadInput,
  TagOwnCollabsAndAdvanceRead,
  UpdateLocalProductReadMarker,
} from "./use-session-read-tracking.types";

interface UseSessionOwnCollabReadAdvancementInput {
  currentUserId: string | null | undefined;
  addOwnMessageIds: AddOwnMessageIds;
  updateLocalProductReadMarker: UpdateLocalProductReadMarker;
}

export function useSessionOwnCollabReadAdvancement(
  input: UseSessionOwnCollabReadAdvancementInput,
): TagOwnCollabsAndAdvanceRead {
  const { currentUserId, addOwnMessageIds, updateLocalProductReadMarker } =
    input;

  return useCallback(
    (actionInput: TagOwnCollabsAndAdvanceReadInput): void => {
      const { productId, collabs } = actionInput;
      if (!currentUserId) return;

      const ownCollabs = collabs.filter(
        (collab) => collab.userAuthor === currentUserId,
      );
      if (ownCollabs.length === 0) return;

      addOwnMessageIds(ownCollabs.map((collab) => collab.id));
      updateLocalProductReadMarker({
        productId,
        collabsReadAt: selectAdvancedOwnCollabReadTime({ collabs: ownCollabs }),
      });
    },
    [addOwnMessageIds, currentUserId, updateLocalProductReadMarker],
  );
}

function selectAdvancedOwnCollabReadTime(input: {
  collabs: readonly Collab[];
}): Date {
  const latestCreatedAt = input.collabs.reduce<Date>((latest, collab) => {
    const created = new Date(collab.createdAt);
    return created.getTime() > latest.getTime() ? created : latest;
  }, new Date(0));

  return new Date(
    latestCreatedAt.getTime() + OWN_COLLAB_READ_ADVANCE_BUFFER_MS,
  );
}
